/**
 * Client-side input validation for typed answers — mirrors the engine's
 * `validation.format` contract so residents get instant feedback before a
 * round-trip. Ported from the live HTML app.
 */
export type ValidationRule = { format?: string } | null | undefined;
export type ValidationResult = { valid: true } | { valid: false; error: string };

const POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const POSTCODE_DISTRICT = /^[A-Z]{1,2}\d[A-Z\d]?$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[0-9+\s().-]{7,20}$/;
const INTEGER = /^\d+$/;
const DOB = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/**
 * Single source of truth for the conversation input path: one message per
 * format token. The backend config carries only the format token (e.g.
 * `Q_POSTCODE` → `{"format": "uk_postcode"}`); the resident-facing message for
 * each token lives here and nowhere else. Regexes are UK-specific — logic
 * unchanged, this table only owns the copy.
 *
 * `uk_date_of_birth` is a multi-branch validation (bad format / impossible date
 * / future / pre-1900), so its messages live in `validateDob`; it has no single
 * message and is intentionally absent from this table.
 */
const FORMAT_RULES: Record<string, { test: (v: string) => boolean; message: string }> = {
  uk_postcode: {
    test: (v) => POSTCODE.test(v),
    message: "Please enter a valid UK postcode (e.g. LE1 1AD).",
  },
  uk_postcode_district: {
    test: (v) => POSTCODE_DISTRICT.test(v),
    message: "Please enter a valid postcode district (e.g. LE1).",
  },
  email: {
    test: (v) => EMAIL.test(v),
    message: "Please enter a valid email address.",
  },
  phone: {
    test: (v) => PHONE.test(v),
    message: "Please enter a valid phone number.",
  },
  integer: {
    test: (v) => INTEGER.test(v),
    message: "Please enter a valid whole number.",
  },
  date: {
    test: (v) => !Number.isNaN(Date.parse(v)),
    message: "Please enter a valid date.",
  },
};

export function validateInput(value: string, rule: ValidationRule): ValidationResult {
  if (!rule?.format) return { valid: true };
  const v = value.trim();

  // Multi-branch validation with per-branch messages — handled separately.
  if (rule.format === "uk_date_of_birth") return validateDob(v);

  const spec = FORMAT_RULES[rule.format];
  if (!spec) return { valid: true }; // unknown token → no client-side constraint
  return spec.test(v) ? { valid: true } : { valid: false, error: spec.message };
}

function validateDob(v: string): ValidationResult {
  const m = v.match(DOB);
  if (!m) {
    return { valid: false, error: "Please enter your date of birth in DD/MM/YYYY format." };
  }
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  const date = new Date(year, month - 1, day);
  const realDate =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  if (!realDate) return { valid: false, error: "Please enter a valid date." };
  if (date > new Date()) return { valid: false, error: "Date of birth cannot be in the future." };
  if (year < 1900) return { valid: false, error: "Please enter a realistic date of birth." };
  return { valid: true };
}

/** Convert an ISO date (YYYY-MM-DD, from a date picker) to DD/MM/YYYY. */
export function isoToUkDate(iso: string): string {
  const parts = iso.split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : iso;
}
